import React from 'react';
import { SchematicShell, Rect, Line } from './SchematicPrimitives';

/**
 * Elevation schematics for dorm beds (single / loft / bunk).
 *
 * Bedding here is a DELIBERATELY SIMPLIFIED stand-in: a pillow rect and a
 * single line. The 3D bedding is sculpted (see dormBedding/ —
 * draped duvet, turned-back sheet, superellipse pillows) and these two no
 * longer match. That divergence is a decision, not an oversight; nothing
 * guards it, because proceduralSchematicSync.test.jsx has never covered
 * dorm types. The FRAME constants below do still track the renderer.
 *
 * Front = looking at the narrow end (width × height); side = looking at the
 * long edge (depth × height).
 */

const IN = 0.0254;

const MATTRESS_H = 8 * IN;
const DECK_H = 0.5 * IN;
const POST_SIZE = 1.5 * IN;
const RAIL_H = 3 * IN;
const RAIL_W = 1.5 * IN;
const PILLOW_W = 20 * IN;
const PILLOW_H = 3 * IN;
const PILLOW_D = 12 * IN;
const GUARDRAIL_H = 10 * IN;
const RUNG_SPACING = 12 * IN;
const FRAME_INSET = 4 * IN;
const SINGLE_OVERSHOOT = 3 * IN;

// SVG y from a height-above-floor value (y=0 is SVG top, y=totalH is floor).
const toY = (aboveFloor, totalH) => totalH - aboveFloor;

// ---------------------------------------------------------------------------
// Single dorm bed
// ---------------------------------------------------------------------------

function SingleFront({ railH, width, height, strokeColor, strokeWidth }) {
  const mTop = railH + DECK_H + MATTRESS_H;
  const postTop = mTop + SINGLE_OVERSHOOT;
  const barH = postTop - 1.5 * IN;
  const mW = width - FRAME_INSET;
  const mX = (width - mW) / 2;

  return (
    <g>
      <Rect
        x={0}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={width - POST_SIZE}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={POST_SIZE / 2}
        y={toY(railH + RAIL_H, height)}
        width={width - POST_SIZE}
        height={RAIL_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX}
        y={toY(mTop, height)}
        width={mW}
        height={MATTRESS_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={mX}
        y1={toY(mTop - 1 * IN, height)}
        x2={mX + mW}
        y2={toY(mTop - 1 * IN, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={(width - PILLOW_W) / 2}
        y={toY(mTop + PILLOW_H, height)}
        width={PILLOW_W}
        height={PILLOW_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={POST_SIZE / 2}
        y1={toY(barH, height)}
        x2={width - POST_SIZE / 2}
        y2={toY(barH, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function SingleSide({ railH, depth, height, strokeColor, strokeWidth }) {
  const mTop = railH + DECK_H + MATTRESS_H;
  const postTop = mTop + SINGLE_OVERSHOOT;
  const barH = postTop - 1.5 * IN;
  const mD = depth - FRAME_INSET;
  const mX = (depth - mD) / 2;

  return (
    <g>
      <Rect
        x={0}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={depth - POST_SIZE}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={POST_SIZE / 2}
        y={toY(railH + RAIL_H, height)}
        width={depth - POST_SIZE}
        height={RAIL_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX}
        y={toY(mTop, height)}
        width={mD}
        height={MATTRESS_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={mX}
        y1={toY(mTop - 1 * IN, height)}
        x2={mX + mD}
        y2={toY(mTop - 1 * IN, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX + 1 * IN}
        y={toY(mTop + PILLOW_H, height)}
        width={PILLOW_D}
        height={PILLOW_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={POST_SIZE / 2}
        y1={toY(barH, height)}
        x2={depth - POST_SIZE / 2}
        y2={toY(barH, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Loft bed — elevated platform with ladder rungs below
// ---------------------------------------------------------------------------

function LoftFront({ railH, width, height, strokeColor, strokeWidth }) {
  const mTop = railH + DECK_H + MATTRESS_H;
  const postTop = mTop + GUARDRAIL_H;
  const mW = width - FRAME_INSET;
  const mX = (width - mW) / 2;

  const rungs = [];
  let rH = RUNG_SPACING;
  let i = 0;
  while (rH <= railH) {
    rungs.push(
      <Line
        key={i}
        x1={POST_SIZE / 2}
        y1={toY(rH, height)}
        x2={width - POST_SIZE / 2}
        y2={toY(rH, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
    rH += RUNG_SPACING;
    i++;
  }

  return (
    <g>
      <Rect
        x={0}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={width - POST_SIZE}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {rungs}
      <Rect
        x={POST_SIZE / 2}
        y={toY(railH + RAIL_H, height)}
        width={width - POST_SIZE}
        height={RAIL_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={POST_SIZE / 2}
        y={toY(postTop, height)}
        width={width - POST_SIZE}
        height={RAIL_W}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX}
        y={toY(mTop, height)}
        width={mW}
        height={MATTRESS_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={mX}
        y1={toY(mTop - 1 * IN, height)}
        x2={mX + mW}
        y2={toY(mTop - 1 * IN, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={(width - PILLOW_W) / 2}
        y={toY(mTop + PILLOW_H, height)}
        width={PILLOW_W}
        height={PILLOW_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function LoftSide({ railH, depth, height, strokeColor, strokeWidth }) {
  const mTop = railH + DECK_H + MATTRESS_H;
  const postTop = mTop + GUARDRAIL_H;
  const mD = depth - FRAME_INSET;
  const mX = (depth - mD) / 2;
  const stabH = railH * 0.4;

  return (
    <g>
      <Rect
        x={0}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={depth - POST_SIZE}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Stabilizer bar */}
      <Rect
        x={POST_SIZE / 2}
        y={toY(stabH + RAIL_W / 2, height)}
        width={depth - POST_SIZE}
        height={RAIL_W}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={POST_SIZE / 2}
        y={toY(railH + RAIL_H, height)}
        width={depth - POST_SIZE}
        height={RAIL_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={POST_SIZE / 2}
        y={toY(postTop, height)}
        width={depth - POST_SIZE}
        height={RAIL_W}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX}
        y={toY(mTop, height)}
        width={mD}
        height={MATTRESS_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={mX}
        y1={toY(mTop - 1 * IN, height)}
        x2={mX + mD}
        y2={toY(mTop - 1 * IN, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX + 1 * IN}
        y={toY(mTop + PILLOW_H, height)}
        width={PILLOW_D}
        height={PILLOW_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Bunk bed — two stacked decks, safety rail on top only
// ---------------------------------------------------------------------------

function BunkFront({
  bottomRailH,
  topRailH,
  width,
  height,
  strokeColor,
  strokeWidth,
}) {
  const topMTop = topRailH + DECK_H + MATTRESS_H;
  const postTop = topMTop + GUARDRAIL_H;
  const botMTop = bottomRailH + DECK_H + MATTRESS_H;
  const mW = width - FRAME_INSET;
  const mX = (width - mW) / 2;

  return (
    <g>
      <Rect
        x={0}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={width - POST_SIZE}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Bottom bed */}
      <Rect
        x={POST_SIZE / 2}
        y={toY(bottomRailH + RAIL_H, height)}
        width={width - POST_SIZE}
        height={RAIL_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX}
        y={toY(botMTop, height)}
        width={mW}
        height={MATTRESS_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={mX}
        y1={toY(botMTop - 1 * IN, height)}
        x2={mX + mW}
        y2={toY(botMTop - 1 * IN, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={(width - PILLOW_W) / 2}
        y={toY(botMTop + PILLOW_H, height)}
        width={PILLOW_W}
        height={PILLOW_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Top bed */}
      <Rect
        x={POST_SIZE / 2}
        y={toY(topRailH + RAIL_H, height)}
        width={width - POST_SIZE}
        height={RAIL_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX}
        y={toY(topMTop, height)}
        width={mW}
        height={MATTRESS_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={mX}
        y1={toY(topMTop - 1 * IN, height)}
        x2={mX + mW}
        y2={toY(topMTop - 1 * IN, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={(width - PILLOW_W) / 2}
        y={toY(topMTop + PILLOW_H, height)}
        width={PILLOW_W}
        height={PILLOW_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Safety rail at top */}
      <Rect
        x={POST_SIZE / 2}
        y={toY(postTop, height)}
        width={width - POST_SIZE}
        height={RAIL_W}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function BunkSide({
  bottomRailH,
  topRailH,
  depth,
  height,
  strokeColor,
  strokeWidth,
}) {
  const topMTop = topRailH + DECK_H + MATTRESS_H;
  const postTop = topMTop + GUARDRAIL_H;
  const botMTop = bottomRailH + DECK_H + MATTRESS_H;
  const mD = depth - FRAME_INSET;
  const mX = (depth - mD) / 2;

  return (
    <g>
      <Rect
        x={0}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={depth - POST_SIZE}
        y={toY(postTop, height)}
        width={POST_SIZE}
        height={postTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Bottom bed */}
      <Rect
        x={POST_SIZE / 2}
        y={toY(bottomRailH + RAIL_H, height)}
        width={depth - POST_SIZE}
        height={RAIL_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX}
        y={toY(botMTop, height)}
        width={mD}
        height={MATTRESS_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={mX}
        y1={toY(botMTop - 1 * IN, height)}
        x2={mX + mD}
        y2={toY(botMTop - 1 * IN, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX + 1 * IN}
        y={toY(botMTop + PILLOW_H, height)}
        width={PILLOW_D}
        height={PILLOW_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Top bed */}
      <Rect
        x={POST_SIZE / 2}
        y={toY(topRailH + RAIL_H, height)}
        width={depth - POST_SIZE}
        height={RAIL_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX}
        y={toY(topMTop, height)}
        width={mD}
        height={MATTRESS_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={mX}
        y1={toY(topMTop - 1 * IN, height)}
        x2={mX + mD}
        y2={toY(topMTop - 1 * IN, height)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={mX + 1 * IN}
        y={toY(topMTop + PILLOW_H, height)}
        width={PILLOW_D}
        height={PILLOW_H}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Safety rail at top */}
      <Rect
        x={POST_SIZE / 2}
        y={toY(postTop, height)}
        width={depth - POST_SIZE}
        height={RAIL_W}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Factory — produces a schematic component with variant info baked in
// ---------------------------------------------------------------------------

function makeDormSchematic(family, railBottomH, topRailH) {
  function DormBedSchematic({
    widthMeters: width,
    heightMeters: height,
    depthMeters: depth,
    view = 'front',
    strokeColor,
    strokeWidth,
  }) {
    if (view === 'left' || view === 'right') {
      const props = { depth, height, strokeColor, strokeWidth };
      let content;
      if (family === 'single')
        content = <SingleSide railH={railBottomH} {...props} />;
      else if (family === 'loft')
        content = <LoftSide railH={railBottomH} {...props} />;
      else
        content = (
          <BunkSide bottomRailH={railBottomH} topRailH={topRailH} {...props} />
        );

      return (
        <SchematicShell
          width={depth}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
          drawFrame={false}
        >
          <g
            transform={
              view === 'right'
                ? `scale(-1, 1) translate(${-depth}, 0)`
                : undefined
            }
          >
            {content}
          </g>
        </SchematicShell>
      );
    }

    const props = { width, height, strokeColor, strokeWidth };
    let content;
    if (family === 'single')
      content = <SingleFront railH={railBottomH} {...props} />;
    else if (family === 'loft')
      content = <LoftFront railH={railBottomH} {...props} />;
    else
      content = (
        <BunkFront bottomRailH={railBottomH} topRailH={topRailH} {...props} />
      );

    return (
      <SchematicShell
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        drawFrame={false}
      >
        {content}
      </SchematicShell>
    );
  }

  DormBedSchematic.supportsSideViews = true;
  return DormBedSchematic;
}

// 7 unique components covering all 14 dorm bed type keys
export const DormBedLowSchematic = makeDormSchematic('single', 9 * IN);
export const DormBedStandardSchematic = makeDormSchematic('single', 24 * IN);
export const DormBedCaptainSchematic = makeDormSchematic('single', 31 * IN);
export const DormLoftSchematic = makeDormSchematic('loft', 57 * IN);
export const DormBunkLowSchematic = makeDormSchematic('bunk', 9 * IN, 57 * IN);
export const DormBunkStandardSchematic = makeDormSchematic(
  'bunk',
  24 * IN,
  57 * IN
);
export const DormBunkCaptainSchematic = makeDormSchematic(
  'bunk',
  31 * IN,
  57 * IN
);
